import { useEffect, useState } from 'react';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseConfig';

export function useExerciseLibrary(trainerId, trainerEmail) {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleExCreated = (e) => {
      setExercises(prev => {
        if (prev.some(x => x.id === e.detail.id)) return prev;
        return [...prev, e.detail].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      });
    };
    const handleExDeleted = (e) => {
      setExercises(prev => prev.filter(ex => ex.id !== e.detail));
    };
    const handleExUpdated = (e) => {
      const updatedEx = e.detail;
      setExercises(prev => prev.map(ex => ex.id === updatedEx.id ? updatedEx : ex).sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    };

    window.addEventListener('custom_exercise_created', handleExCreated);
    window.addEventListener('custom_exercise_deleted', handleExDeleted);
    window.addEventListener('custom_exercise_updated', handleExUpdated);
    window.addEventListener('exercise_created', handleExCreated);
    window.addEventListener('exercise_deleted', handleExDeleted);
    window.addEventListener('exercise_updated', handleExUpdated);
    return () => {
      window.removeEventListener('custom_exercise_created', handleExCreated);
      window.removeEventListener('custom_exercise_deleted', handleExDeleted);
      window.removeEventListener('custom_exercise_updated', handleExUpdated);
      window.removeEventListener('exercise_created', handleExCreated);
      window.removeEventListener('exercise_deleted', handleExDeleted);
      window.removeEventListener('exercise_updated', handleExUpdated);
    };
  }, []);

  useEffect(() => {
    let unsubscribe;
    try {
      const q = query(collection(db, 'exercises'));

      unsubscribe = onSnapshot(q, (snapshot) => {
        const allDocs = snapshot.docs.map(docSnap => {
          const data = docSnap.data();
          return {
            ...data,
            id: docSnap.id,
            docId: docSnap.id
          };
        });
        const cleanId = (trainerId || "").toLowerCase().trim();
        const cleanEmail = (trainerEmail || "").toLowerCase().trim();

        // Pure live database filtering: Global exercises created by Super Admin (trainerId === 'global' || isGlobal) + assigned coach exercises
        const filtered = allDocs.filter(ex => {
          const owner = (ex.trainerId || "").toLowerCase().trim();
          const isGlobal = ex.isGlobal === true || owner === 'global' || owner === 'all' || !owner;
          if (isGlobal) return true;
          if (cleanId && owner === cleanId) return true;
          if (cleanEmail && owner === cleanEmail) return true;
          return false;
        });

        // Deduplicate exercises by clean id & name
        const uniqueExercises = [];
        const seen = new Set();
        for (const ex of filtered) {
          const key = ex.id || (ex.name || "").toLowerCase().trim();
          if (key && !seen.has(key)) {
            seen.add(key);
            uniqueExercises.push(ex);
          }
        }

        uniqueExercises.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setExercises(uniqueExercises);
        setLoading(false);
      }, (error) => {
        console.warn("Error loading exercise library from Firestore:", error.message);
        setLoading(false);
      });
    } catch (err) {
      setLoading(false);
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [trainerId, trainerEmail]);

  return { exercises, loading };
}
